import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import { apiGet, apiPost, apiPatch } from '../../utilities/apiClient';
import { getSessionIds } from './_sessionIds';
import { ADD_NOTES, FETCHING_NOTES, FETCHED_NOTES, UPDATE_PROJECT, DOCUMENTS_DIRTY } from '../constants';

const isAndroidContentUri = (uri: string) =>
  typeof uri === 'string' && uri.startsWith('content://');

// On web/desktop the upload sources (Google Drive picker, Dropbox chooser,
// browser file input) all hand us either a Blob-backed URL or a data URL.
// FileSystem.uploadAsync from expo-file-system has no web implementation,
// so we use fetch() to PUT the bytes to the presigned S3 URL instead.
const isWebBlobOrDataUri = (uri: string) =>
  typeof uri === 'string' && (uri.startsWith('blob:') || uri.startsWith('data:'));

const shouldUploadViaFetch = (uri: string) =>
  Platform.OS === 'web' || isWebBlobOrDataUri(uri);

const buildUploadCachePath = (filename: string) => {
  const baseDir = FileSystem.cacheDirectory || FileSystem.documentDirectory;
  if (!baseDir) {
    throw new Error('No writable cache directory available');
  }
  const safeName = (filename || `upload-${Date.now()}`).replace(/[\\/:*?"<>|]/g, '-');
  return `${baseDir}${Date.now()}-${safeName}`;
};

const resolveUploadUri = async (
  uri: string,
  filename: string,
): Promise<{ uploadUri: string; cleanupUri: string | null }> => {
  if (!isAndroidContentUri(uri)) {
    return { uploadUri: uri, cleanupUri: null };
  }
  const copyPath = buildUploadCachePath(filename);
  await FileSystem.copyAsync({ from: uri, to: copyPath });
  return { uploadUri: copyPath, cleanupUri: copyPath };
};

export const fetchNotes = (projectId: number | string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId || projectId == null) return;
  dispatch({ type: FETCHING_NOTES, payload: true });
  try {
    const res = await apiGet(`/notes/get-all/${projectId}/${accountId}/${cognitoId}`);
    const data = res?.payload ?? res?.data ?? res;
    const list = Array.isArray(data) ? data : [];
    dispatch({ type: ADD_NOTES, payload: { project_id: String(projectId), list } });
    dispatch({ type: FETCHED_NOTES, payload: { project_id: String(projectId) } });
    dispatch({ type: UPDATE_PROJECT, payload: { id: projectId, note_count: list.length } });
  } catch {
    dispatch({ type: ADD_NOTES, payload: { project_id: String(projectId), list: [] } });
    dispatch({ type: FETCHED_NOTES, payload: { project_id: String(projectId) } });
  } finally {
    dispatch({ type: FETCHING_NOTES, payload: false });
  }
};

export const createNote = (projectId: number | string, data: {
  body?: string;
  raw?: string;
  type: 'voice' | 'text';
  query?: string;
  title?: string;
  template_id?: number | string | null;
  form_data?: Record<string, any> | null;
}) => async () => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiPost(
      `/notes/create/${projectId}/${accountId}/${cognitoId}`,
      { note: data }
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not save note.' };
  }
};

/**
 * Manually resolve or dismiss a care_gap model-memory note.
 *
 * Hits PATCH /notes/resolve-care-gap/{note_id}/{account_id}/{cognito_id}.
 * Backend cascades to expire any pending agent_proposed_actions linked via
 * source_entity_id, so the user does not need to dismiss them separately.
 */
export const resolveCareGap = (
  noteId: number | string,
  options: { status: 'resolved' | 'dismissed'; reason?: string | null; note?: string | null }
) => async () => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId || noteId == null) {
    return { success: false, message: 'Missing session.' };
  }
  try {
    const response = await apiPatch(
      `/notes/resolve-care-gap/${noteId}/${accountId}/${cognitoId}`,
      {
        status: options.status,
        reason: options.reason ?? null,
        note: options.note ?? null,
      }
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not resolve care gap.' };
  }
};

export const updateDocument = (
  projectId: number | string,
  documentId: number | string,
  updates: {
    filename?: string;
    description?: string;
    emergency?: boolean;
    document_type?: string;
    kb_access?: boolean;
  },
  newFile?: { uri: string; mimeType: string; name: string; size: number; existingFilename: string } | null,
) => async (): Promise<{ success: boolean; message?: string; payload?: any }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    // When replacing the file, we must upload the new bytes to the existing S3 key
    // BEFORE calling the update endpoint so that when the backend queues re-processing
    // it finds the correct bytes at that path.
    if (newFile) {
      const signedRes = await apiPost(
        `/documents/get-signed-url/${accountId}/${cognitoId}`,
        {
          config: {
            filename: newFile.existingFilename,
            project_id: projectId,
            account_id: accountId,
          },
          method: 'put',
        },
      );
      if (!signedRes?.success || !signedRes?.payload) {
        return { success: false, message: 'Failed to get upload URL for file replacement.' };
      }
      // Web/native split: same rationale as the new-document branch in
      // uploadDocument — FileSystem.uploadAsync has no web build, so on web
      // we read the source URI as a Blob and PUT via fetch() instead.
      if (shouldUploadViaFetch(newFile.uri)) {
        const blob = await fetch(newFile.uri).then((r) => {
          if (!r.ok) throw new Error(`Failed to read replacement source (${r.status})`);
          return r.blob();
        });
        const uploadRes = await fetch(signedRes.payload, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': newFile.mimeType },
        });
        if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function' && newFile.uri.startsWith('blob:')) {
          try { URL.revokeObjectURL(newFile.uri); } catch { /* ignore */ }
        }
        if (!uploadRes.ok) {
          return { success: false, message: 'Failed to upload replacement file.' };
        }
      } else {
        const { uploadUri, cleanupUri } = await resolveUploadUri(newFile.uri, newFile.name || newFile.existingFilename || 'replacement-file');
        const uploadRes = await FileSystem.uploadAsync(signedRes.payload, uploadUri, {
          httpMethod: 'PUT',
          headers: { 'Content-Type': newFile.mimeType },
        });
        if (cleanupUri) {
          await FileSystem.deleteAsync(cleanupUri, { idempotent: true }).catch(() => {});
        }
        if (uploadRes.status !== 200) {
          return { success: false, message: 'Failed to upload replacement file.' };
        }
      }
    }

    const new_file = newFile
      ? { type: newFile.mimeType, size: newFile.size, name: newFile.name }
      : null;

    const res = await apiPatch(
      `/documents/update/${projectId}/${documentId}/${accountId}/${cognitoId}`,
      { updates, new_file },
    );
    if (!res?.success) return { success: false, message: res?.message || 'Failed to update document.' };

    return { success: true, payload: res.payload };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Update failed.' };
  }
};

export const uploadDocument = (projectId: number | string, fileConfig: {
  filename: string;
  name: string;
  document_type?: string;
  kb_access?: boolean;
  size?: number;
  type?: string;
  mime?: string;
  project_id?: number | string;
  /** Links a chat-uploaded file to its conversation so it renders in the chat history. */
  conversation_id?: string | null;
}, fileUri: string, fileMimeType: string) => async (dispatch: any): Promise<{ success: boolean; message?: string; payload?: any }> => {
  const { accountId, cognitoId } = getSessionIds();

  try {
    const config = {
      ...fileConfig,
      filename: fileConfig.filename.replace(/\//g, '-'),
      project_id: projectId,
    };

    // Step 1: Get a presigned S3 PUT URL (no document record created yet)
    const signedRes = await apiPost(
      `/documents/get-signed-url/${accountId}/${cognitoId}`,
      { config, method: 'put' }
    );
    if (!signedRes?.success || !signedRes?.payload) {
      return { success: false, message: 'Failed to get upload URL.' };
    }

    // Step 2: Upload the file to S3.
    // Two paths because expo-file-system's uploadAsync has no web build:
    //   • Native (file://, content://, ph://) — FileSystem.uploadAsync streams
    //     bytes from the URI directly, no in-memory copy. BINARY_CONTENT is
    //     required for S3 presigned PUT URLs (multipart would corrupt the
    //     object). Android content:// URIs need to be copied into the cache
    //     directory first because the native uploader can't read them.
    //   • Web (blob:, data:, or anything else when Platform.OS === 'web') —
    //     fetch the bytes into a Blob and PUT them via fetch(). Handles every
    //     web upload source: Google Picker, Dropbox Chooser, expo-document-picker
    //     (which on web wraps the browser file input). Same end result on S3,
    //     just a different transport because there's no native bridge to use.
    if (shouldUploadViaFetch(fileUri)) {
      const blob = await fetch(fileUri).then((r) => {
        if (!r.ok) throw new Error(`Failed to read upload source (${r.status})`);
        return r.blob();
      });
      const uploadRes = await fetch(signedRes.payload, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': fileMimeType },
      });
      // Best-effort: revoke the object URL so the picker-allocated blob can
      // be GC'd. No-op for data: URIs and harmless if the caller already
      // revoked it.
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function' && fileUri.startsWith('blob:')) {
        try { URL.revokeObjectURL(fileUri); } catch { /* ignore */ }
      }
      if (!uploadRes.ok) {
        return { success: false, message: 'Failed to upload file to storage.' };
      }
    } else {
      const { uploadUri, cleanupUri } = await resolveUploadUri(fileUri, config.filename);
      const uploadRes = await FileSystem.uploadAsync(signedRes.payload, uploadUri, {
        httpMethod: 'PUT',
        uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
        headers: { 'Content-Type': fileMimeType },
      });
      if (cleanupUri) {
        await FileSystem.deleteAsync(cleanupUri, { idempotent: true }).catch(() => {});
      }
      if (uploadRes.status !== 200) {
        return { success: false, message: 'Failed to upload file to storage.' };
      }
    }

    // Step 3: Create the document record now that the file is on S3.
    // This queues it for processing — file is guaranteed to exist at this point.
    const createRes = await apiPost(
      `/documents/create/${accountId}/${cognitoId}`,
      { file: config }
    );
    if (!createRes?.success) {
      return { success: false, message: createRes?.message || 'Failed to create document record.' };
    }

    // Notify any screen that watches the documents-dirty counter for
    // this project (primarily DocumentsScreen). Without this, uploads
    // from ChatScreen / EllieVoiceScreen / KB surfaces don't refresh
    // the Documents tab until useIsFocused transitions — which was
    // proving unreliable on Android tab navigation (ED-2048).
    dispatch({ type: DOCUMENTS_DIRTY, payload: { project_id: projectId } });

    return { success: true, payload: createRes.payload };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Upload failed.' };
  }
};
